import React from "react";
import { motion } from "framer-motion";
import { Flame, Plus } from "lucide-react";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";

export default function ProductCard({ product, onClick }) {
    const [imageError, setImageError] = React.useState(false);
    
    // Check if product has variants
    const hasVariants = product.variants && product.variants.length > 0;
    const isOutOfStock = hasVariants 
        ? !product.variants.some(v => v.stock > 0) 
        : product.stock <= 0;
        
    const displayStock = hasVariants 
        ? product.variants.reduce((acc, v) => acc + Number(v.stock), 0)
        : product.stock;

    const displayPrice = hasVariants
        ? Math.min(...product.variants.map(v => Number(v.unit_price)))
        : product.price;

    return (
        <motion.div
            whileHover={!isOutOfStock ? { y: -5 } : {}}
            whileTap={!isOutOfStock ? { scale: 0.98 } : {}}
            onClick={() => !isOutOfStock && onClick(product)}
            className={`bg-white rounded-3xl p-5 shadow-sm border border-transparent transition-all flex flex-col items-center relative group
                ${isOutOfStock ? 'opacity-60 cursor-not-allowed grayscale' : 'hover:shadow-xl hover:border-green-100 cursor-pointer'}
            `}
        >
            {/* Stock Badge */}
            <div className={`absolute top-4 right-4 px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1
                ${isOutOfStock ? 'bg-gray-200 text-gray-500' : 'bg-green-50 text-green-600'}
            `}>
                <span>{isOutOfStock ? 'No Stock' : `${displayStock} Left`}</span>
            </div>

            {/* Dynamic Badge (Popular/Recommended/Special) */}
            {(product.is_popular || product.is_recommended || product.is_special) && (
                <div className={`absolute top-4 left-4 px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 shadow-sm border
                    ${product.is_popular ? 'bg-green-50 text-green-500 border-green-100' : 
                      product.is_recommended ? 'bg-blue-50 text-blue-500 border-blue-100' : 
                      'bg-purple-50 text-purple-500 border-purple-100'}
                `}>
                    {product.is_popular && <Flame className="w-3 h-3 fill-green-500" />}
                    <span>
                        {product.is_popular ? 'Most Popular' : 
                         product.is_recommended ? 'Recommended' : 
                         'Special Offer'}
                    </span>
                </div>
            )}

            {/* Image Area - Standardised Fixed Size */}
            <div className="w-full h-48 rounded-2xl bg-gray-100 flex items-center justify-center overflow-hidden transition-all duration-300 group-hover:scale-[1.02]">
                {product.image && !imageError ? (
                    <img
                        src={resolveMediaUrl(product.image)}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            setImageError(true);
                        }}
                    />
                ) : (
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                        <span className="text-5xl">🥘</span>
                        <span className="text-[10px] font-bold uppercase tracking-tight">No Image</span>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="w-full text-center mt-3">
                <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2 min-h-[2rem] items-center flex justify-center leading-tight">
                    {product.name}
                </h3>
                {product.asn_code && (
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mb-2">
                        ASN: {product.asn_code}
                    </p>
                )}

                <div className="flex items-end justify-between w-full mt-2">
                    <div className="flex flex-col">
                        <div className="flex items-baseline gap-1">
                            {hasVariants && <span className="text-xs text-gray-500 font-medium mr-1">From</span>}
                            <span className="text-2xl font-bold text-gray-900 leading-none">
                                ₹{displayPrice.toLocaleString()}
                            </span>
                        </div>
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1">
                            {hasVariants ? 'multiple options' : `per ${product.unit || 'unit'}`}
                        </span>
                    </div>

                    <button
                        disabled={isOutOfStock}
                        className={`w-10 h-10 rounded-xl text-white flex items-center justify-center transition-colors shadow-lg
                            ${isOutOfStock ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 shadow-green-200'}
                        `}
                    >
                        <Plus className="w-6 h-6" />
                    </button>
                </div>
            </div>
        </motion.div>
    );
}
